import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PublicCanvasArtifact, PublicCanvasFile } from "./types";

export type PublicCanvasArtifactContextValue = {
  artifact: PublicCanvasArtifact;
  /** True when this tree is the public share SPA (no API / Liveblocks). */
  isPublicCanvasView: true;
  /** CDN origin the artifact's object keys resolve against. */
  shareOrigin: string;
  openFileId: string | null;
  openFile: PublicCanvasFile | null;
  openPublicFile: (fileRecordId: string) => void;
  closePublicFile: () => void;
};

const PublicCanvasArtifactContext =
  createContext<PublicCanvasArtifactContextValue | null>(null);

export function PublicCanvasArtifactProvider({
  artifact,
  shareOrigin,
  children,
}: {
  artifact: PublicCanvasArtifact;
  shareOrigin: string;
  children: ReactNode;
}) {
  const [openFileId, setOpenFileId] = useState<string | null>(null);

  const openPublicFile = useCallback(
    (fileRecordId: string) => {
      if (!fileRecordId) return;
      if (!artifact.files[fileRecordId]) return;
      setOpenFileId(fileRecordId);
    },
    [artifact.files],
  );

  const closePublicFile = useCallback(() => {
    setOpenFileId(null);
  }, []);

  const openFile = openFileId ? (artifact.files[openFileId] ?? null) : null;

  const value = useMemo(
    (): PublicCanvasArtifactContextValue => ({
      artifact,
      isPublicCanvasView: true,
      shareOrigin,
      openFileId,
      openFile,
      openPublicFile,
      closePublicFile,
    }),
    [
      artifact,
      shareOrigin,
      openFileId,
      openFile,
      openPublicFile,
      closePublicFile,
    ],
  );

  return (
    <PublicCanvasArtifactContext.Provider value={value}>
      {children}
    </PublicCanvasArtifactContext.Provider>
  );
}

export function usePublicCanvasArtifact(): PublicCanvasArtifactContextValue {
  const ctx = useContext(PublicCanvasArtifactContext);
  if (!ctx) {
    throw new Error(
      "usePublicCanvasArtifact must be used within PublicCanvasArtifactProvider",
    );
  }
  return ctx;
}

/** Safe read for hooks that also run in the authenticated app. */
export function usePublicCanvasArtifactSafe(): PublicCanvasArtifactContextValue | null {
  return useContext(PublicCanvasArtifactContext);
}

export function useIsPublicCanvasView(): boolean {
  return useContext(PublicCanvasArtifactContext)?.isPublicCanvasView === true;
}
